import { useState, useEffect, useRef } from "react";
import { Link } from "wouter";
import PageMeta from "../components/PageMeta";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { ArrowLeft, ArrowRight, Infinity, Shield, TrendingDown, Users, Heart, PawPrint } from "lucide-react";
import Lenis from "lenis";

// ── Loyalty model ──────────────────────────────────────────────────────────
// Founding member schedule (first 2,000 members): 10%/yr → 90% cap at Year 9.
// Standard schedule (post-founding): 8%/yr → 90% cap at Year 12.
// The simulator below shows the founding schedule — the better deal available now.
const LOYALTY_TIERS = [
  { year: 1, bonus: 10, label: "Welcomed",  colour: "bg-muted" },
  { year: 2, bonus: 20, label: "Known",     colour: "bg-secondary/20" },
  { year: 3, bonus: 30, label: "Familiar",  colour: "bg-secondary/35" },
  { year: 4, bonus: 40, label: "Settled",   colour: "bg-secondary/50" },
  { year: 5, bonus: 50, label: "Bonded",    colour: "bg-primary/30" },
  { year: 6, bonus: 60, label: "Devoted",   colour: "bg-primary/45" },
  { year: 7, bonus: 70, label: "Trusted",   colour: "bg-primary/60" },
  { year: 8, bonus: 80, label: "Cherished", colour: "bg-primary/75" },
  { year: 9, bonus: 90, label: "Always",    colour: "bg-primary" },
];

function getBonusForYear(year: number) {
  let bonus = 0;
  for (const t of LOYALTY_TIERS) { if (year >= t.year) bonus = t.bonus; }
  return bonus;
}
function getTierForYear(year: number) {
  let tier = LOYALTY_TIERS[0];
  for (const t of LOYALTY_TIERS) { if (year >= t.year) tier = t; }
  return tier;
}
function getNextTier(year: number) {
  return LOYALTY_TIERS.find(t => t.year > year) ?? null;
}

const AVG_ANNUAL_SPEND = 4800; // SGD — avg hospital spend per pet per year

function calcCumulative(year: number) {
  let total = 0;
  for (let y = 1; y <= year; y++) total += (AVG_ANNUAL_SPEND * getBonusForYear(y)) / 100;
  return Math.round(total);
}

// ── Multi-pet model ────────────────────────────────────────────────────────
const PLAN_PRICES: Record<string, number> = { Steadfast: 680, Devoted: 1180, Besotted: 2980 };

function petDiscount(idx: number) { return idx === 0 ? 0 : idx === 1 ? 20 : idx === 2 ? 25 : 30; }

// Fixed 5+ household pack prices (annual, covers unlimited pets in one household)
const PACK_PRICES: Record<string, number> = { Steadfast: 2_680, Devoted: 4_480, Besotted: 10_800 };

// ── Plan data (whimsical names) ───────────────────────────────────────────
const PLANS = [
  {
    name: "Steadfast",
    whimsy: "The Steadfast",
    tagline: "For the conscientious furchild parent",
    price: "S$680",
    monthly: "S$57",
    features: [
      "Annual wellness examination",
      "Devoted vaccinations",
      "Genomics baseline screening",
      "Same-week appointment guarantee",
      "Personalised health record, accessible anytime",
    ],
  },
  {
    name: "Devoted",
    whimsy: "The Devoted",
    tagline: "For those who leave nothing to chance",
    price: "S$1,180",
    monthly: "S$98",
    tag: "Most Popular",
    features: [
      "Bi-annual examinations",
      "Comprehensive bloodwork panel",
      "Advanced Genomics Panel — included",
      "One urgent care consultation per year",
      "Dental health screening",
      "Priority 24hr booking",
    ],
  },
  {
    name: "Besotted",
    whimsy: "The Besotted",
    tagline: "For parents who know no bounds",
    price: "S$2,980",
    monthly: "S$248",
    tag: "All-inclusive",
    features: [
      "Weekly consultations",
      "Full diagnostic suite access",
      "Full Genomics Workup — included",
      "24/7 veterinary care line",
      "Annual dental procedure",
      "Daily wellness checks and specialist access",
    ],
  },
];

export default function Membership() {
  const [loyaltyYear, setLoyaltyYear] = useState(1);
  const [numPets, setNumPets] = useState(1);
  const [petPlans, setPetPlans] = useState<string[]>(["Devoted", "Steadfast", "Steadfast", "Steadfast"]);
  const [packPlan, setPackPlan] = useState("Devoted");
  const [endOfLifeOpen, setEndOfLifeOpen] = useState(false);
  const [pauseOpen, setPauseOpen] = useState(false);
  const [aiCoverOpen, setAiCoverOpen] = useState(false);
  const footerRef = useRef(null);
  const footerInView = useInView(footerRef, { once: true, margin: "-100px" });

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.02, smoothWheel: true });
    let rafId: number;
    function raf(t: number) { lenis.raf(t); rafId = requestAnimationFrame(raf); }
    rafId = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(rafId); lenis.destroy(); };
  }, []);

  const bonus = getBonusForYear(loyaltyYear);
  const tier = getTierForYear(loyaltyYear);
  const nextTier = getNextTier(loyaltyYear);
  const cumulative = calcCumulative(loyaltyYear);

  const isPack   = numPets === 5;
  const petTotal = isPack
    ? PACK_PRICES[packPlan]
    : petPlans.slice(0, numPets).reduce(
        (sum, plan, idx) => sum + PLAN_PRICES[plan] * (1 - petDiscount(idx) / 100), 0
      );
  const petFull  = isPack
    ? PLAN_PRICES[packPlan] * 5
    : petPlans.slice(0, numPets).reduce((sum, plan) => sum + PLAN_PRICES[plan], 0);
  const petSaved = petFull - petTotal;

  const { scrollYProgress } = useScroll();

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden cursor-default selection:bg-primary/40">
      <PageMeta
        title="Membership — Furbaby Vet Care"
        description="Explore Furbaby membership tiers: Steadfast, Devoted, and Besotted. Each plan covers unlimited consultations, preventive care, and genomics-informed wellness for your pet."
        canonical="https://furbaby.vet/main/membership"
        ogTitle="Furbaby Membership Plans"
        ogDescription="Unlimited vet care with a loyalty discount that grows every year. Explore Steadfast, Devoted, and Besotted tiers."
      />
      {/* Scroll progress */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-[1px] bg-primary/60 z-[100] origin-left"
        style={{ scaleX: scrollYProgress }}
      />

      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-6 md:px-12 py-6 flex justify-between items-center">
        <Link href="/" className="group flex items-center gap-2 font-grotesk text-xs uppercase tracking-widest hover:opacity-60 transition-opacity duration-500">
          <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform duration-500" />
          Back
        </Link>
        <span className="font-serif text-2xl font-bold italic tracking-tighter">Furbaby</span>
        <Link href="/book" className="group flex items-center gap-2 font-grotesk text-xs uppercase tracking-widest hover:opacity-60 transition-opacity duration-500">
          Book Visit
          <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform duration-500" />
        </Link>
      </nav>

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="relative flex flex-col justify-center px-4 md:px-20 pt-24 md:pt-32 pb-8 md:pb-24 overflow-hidden md:min-h-screen">
        <div className="absolute inset-0 bg-gradient-to-br from-secondary/20 via-background to-primary/10 pointer-events-none" />
        <motion.div
          className="hidden md:block absolute top-1/2 right-0 w-[42%] h-[90%] -translate-y-1/2 overflow-hidden pointer-events-none rounded-l-3xl"
          initial={{ scaleX: 0 }} animate={{ scaleX: 1 }}
          transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
          style={{ originX: 1 }}
        >
          <img
            src={`/main/membership-hero.png`}
            alt="Veterinarian gently holding a cat"
            className="w-full h-full object-cover opacity-90"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-transparent to-transparent" />
        </motion.div>

        <div className="relative z-10 max-w-5xl">
          <motion.p
            className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-primary mb-8"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Furbaby Membership
          </motion.p>

          <div className="mb-8 md:mb-16 overflow-hidden pb-6">
            <motion.h1
              className="font-serif text-[9vw] md:text-[4.8vw] leading-[0.9] tracking-tighter"
              initial={{ y: "110%" }} animate={{ y: "0%" }}
              transition={{ duration: 1.3, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            >
              Better than insurance.<br />
              <span className="italic text-primary">Complete care, always.</span>
            </motion.h1>
          </div>

          <motion.p
            className="font-sans text-base md:text-2xl max-w-2xl leading-relaxed opacity-70 mt-4"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 0.7, y: 0 }}
            transition={{ duration: 1, delay: 0.8 }}
          >
            A lifetime commitment to your furchild's wellbeing — one that
            deepens with every year you stay. No renewals. No age limits. Just
            care that compounds.
          </motion.p>
        </div>

      </section>

      {/* ── THREE PILLARS ─────────────────────────────────────── */}
      <section className="pt-10 pb-16 md:pt-16 md:pb-32 px-4 md:px-20 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Infinity,    title: "Lifetime",         body: "Your plan doesn't expire when your pet gets older. No age limits, no breed exclusions. Your membership simply continues — for the whole of your furbaby's life." },
              { icon: Shield,      title: "Cost Reduction",   body: "Your membership acts as a direct reduction on what you pay for treatments, procedures, and consultations at Furbaby — including sudden or emergency costs." },
              { icon: TrendingDown, title: "Loyalty That Pays", body: "The longer you stay, the more comes off your bill. Every year of membership deepens your discount — growing steadily until you reach our maximum loyalty rate. Founding members (first 2,000) reach the 90% ceiling at Year 9." },
            ].map((p, i) => (
              <motion.div
                key={p.title}
                className="group p-10 rounded-[2.5rem] bg-background border border-border/40 hover:border-primary/30 relative overflow-hidden transition-all duration-700"
                initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.9, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className="absolute inset-0 rounded-[2.5rem] opacity-0 group-hover:opacity-100 transition-opacity duration-700 bg-gradient-to-br from-primary/14 via-accent/10 to-secondary/14 pointer-events-none" />
                <div className="relative z-10">
                  <p.icon className="w-8 h-8 text-primary mb-8 opacity-70" strokeWidth={1.5} />
                  <h3 className="font-serif text-4xl mb-4 group-hover:italic transition-all duration-500">{p.title}</h3>
                  <p className="font-sans text-base opacity-65 leading-relaxed">{p.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── THE FURBABY PROMISE ───────────────────────────────── */}
      <section className="py-20 md:py-40 px-4 md:px-20 bg-background border-t border-border/30">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="mb-12 md:mb-20"
          >
            <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-primary mb-8">The Furbaby Promise</p>
            <h2 className="font-serif text-6xl md:text-[9vw] tracking-tighter leading-none mb-8">
              Care that<br /><span className="italic text-primary">compounds.</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            >
              <p className="font-sans text-xl leading-relaxed opacity-70 mb-8">
                Every year you remain a Furbaby member, your loyalty is quietly rewarded. As your furchild grows older — and the stakes grow higher — your membership discount on emergency and hospital care at the Furbaby Animal Hospital grows with it.
              </p>
              <p className="font-sans text-base leading-relaxed opacity-55 mb-8">
                Think of it as the opposite of insurance: no premiums that rise with age, no claims that get declined, no fine print. Instead, a deepening relationship that translates directly into savings when your furchild needs it most — a sudden surgery, an emergency consultation, or end-of-life care handled with grace.
              </p>
              <p className="font-sans text-base leading-relaxed opacity-55">
                Combined with annual wellness exams, genomics screenings, and priority access to Furbaby's veterinary team, membership means your furchild is truly seen, known, and protected — year after year.
              </p>
            </motion.div>

            <div className="flex flex-col gap-5">
              {[
                {
                  title: "Emergency savings that grow",
                  body: "Your hospital discount grows with every year of loyalty — reaching up to 90% reduction of bills by Year 9 for founding members. Every year counts: no long plateaux, no waiting. The longer you stay, the lighter the burden when it matters most.",
                },
                {
                  title: "Prevention built in, always",
                  body: "Annual wellness exams, genomics scanning, and comprehensive bloodwork are woven into every plan. We catch what others miss — because reactive care should never be the default.",
                },
                {
                  title: "A community worth belonging to",
                  body: "A gathering of devoted Furbaby parents with run clubs, days out, special discounts on new products and a network that just understands you.",
                },
                {
                  title: "Your furchild, always remembered",
                  body: "Every visit deepens the picture. No repeating yourself, no starting over. A veterinary team that knows your pet's history, temperament, and biology as well as you do.",
                },
              ].map((item, i) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.8, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                  className="p-7 rounded-2xl border border-border/40 bg-muted/20 hover:border-primary/30 transition-colors duration-500"
                >
                  <h4 className="font-serif text-xl mb-2">{item.title}</h4>
                  <p className="font-sans text-sm opacity-60 leading-relaxed">{item.body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── LOYALTY SLIDER ────────────────────────────────────── */}
      <section className="py-20 md:py-40 px-4 md:px-20 bg-background">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="mb-12 md:mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-primary mb-4">Loyalty Rewarded</p>
            <h2 className="font-serif text-6xl md:text-8xl tracking-tighter leading-none">
              Think ahead.<br /><span className="italic text-primary">Breathe easier.</span>
            </h2>
            <p className="font-sans text-sm opacity-55 mt-6 max-w-xl">
              Showing the <strong className="opacity-80">founding member schedule</strong> — available to the first 2,000 members. 10% per year, reaching 90% by Year 9. Members joining after the founding window close on the standard 8%/yr schedule (90% cap at Year 12).
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-start">
            {/* Slider panel */}
            <motion.div
              className="p-6 md:p-10 rounded-[2rem] md:rounded-[3rem] bg-background border border-border/40"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            >
              <p className="font-grotesk text-xs uppercase tracking-[0.25em] opacity-80 mb-4">
                Years as a member
              </p>
              <div className="flex items-baseline gap-3 mb-8">
                <span className="font-serif text-[80px] leading-none text-primary">{loyaltyYear}</span>
                <span className="font-grotesk text-sm uppercase tracking-widest opacity-70">{loyaltyYear === 1 ? "year" : "years"}</span>
              </div>

              <input
                type="range" min={1} max={9} step={1} value={loyaltyYear}
                onChange={e => setLoyaltyYear(Number(e.target.value))}
                className="w-full accent-[hsl(var(--primary))] mb-8"
                style={{ height: "2px" }}
              />

              {/* Tier milestones */}
              <div className="flex justify-between text-[10px] font-grotesk uppercase tracking-widest opacity-75 mb-10">
                {LOYALTY_TIERS.map(t => (
                  <span key={t.year} className={loyaltyYear >= t.year ? "text-foreground opacity-80" : ""}>
                    Y{t.year}
                  </span>
                ))}
              </div>

              {/* Current tier badge */}
              <div className="flex items-center gap-4 p-5 rounded-2xl bg-primary/10 border border-primary/20">
                <div>
                  <p className="font-grotesk text-xs uppercase tracking-widest opacity-80 mb-1">Loyalty Status</p>
                  <p className="font-serif text-2xl text-primary">{tier.label}</p>
                </div>
                <div className="ml-auto text-right">
                  <p className="font-grotesk text-xs uppercase tracking-widest opacity-80 mb-1">Hospital Bonus</p>
                  <p className="font-serif text-3xl text-primary">{bonus > 0 ? `−${bonus}%` : "—"}</p>
                </div>
              </div>

              {nextTier && (
                <p className="font-grotesk text-xs mt-5 opacity-90 text-center">
                  {nextTier.year - loyaltyYear} more {nextTier.year - loyaltyYear === 1 ? "year" : "years"} to reach <span className="text-primary">{nextTier.label}</span> (−{nextTier.bonus}%)
                </p>
              )}
              {!nextTier && (
                <p className="font-grotesk text-xs mt-5 text-primary text-center opacity-70">
                  Maximum loyalty achieved.
                </p>
              )}
            </motion.div>

            {/* Savings panel */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="mb-12">
                <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] opacity-90 mb-3">
                  Estimated cumulative savings
                </p>
                <motion.div
                  key={cumulative}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="font-serif text-7xl md:text-8xl tracking-tighter text-primary"
                >
                  S${cumulative.toLocaleString()}
                </motion.div>
                <p className="font-sans text-sm opacity-80 mt-8">
                  Based on avg. S${AVG_ANNUAL_SPEND.toLocaleString()} annual hospital spend
                </p>
              </div>

              {/* Year-by-year table */}
              <div className="border-t border-border/30 pt-8">
                <p className="font-grotesk text-xs uppercase tracking-widest opacity-100 mb-5">Loyalty milestones</p>
                <div className="flex flex-col gap-3">
                  {LOYALTY_TIERS.map(t => {
                    const reached = loyaltyYear >= t.year;
                    const current = tier.year === t.year;
                    return (
                      <div
                        key={t.year}
                        className={`flex items-center gap-4 py-3 px-4 rounded-xl transition-all duration-500 ${current ? "bg-primary/10 border border-primary/20" : reached ? "opacity-90" : "opacity-60"}`}
                      >
                        <span className="font-grotesk text-xs uppercase tracking-widest w-12 opacity-90">Yr {t.year}</span>
                        <span className={`font-serif text-xl flex-1 ${current ? "text-primary italic" : ""}`}>{t.label}</span>
                        <span className={`font-grotesk text-sm font-medium ${current ? "text-primary" : "opacity-80"}`}>
                          {t.bonus > 0 ? `−${t.bonus}% off all bills` : "Base member rates"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── END-OF-LIFE CARE ──────────────────────────────────── */}
      <section className="py-16 md:py-32 px-4 md:px-20 bg-accent/20 border-y border-border/30">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            <Heart className="w-8 h-8 text-primary mb-8 opacity-60" strokeWidth={1.5} />
            <button
              onClick={() => setEndOfLifeOpen(o => !o)}
              className="text-left group w-full"
            >
              <h2 className="font-serif text-5xl md:text-7xl tracking-tighter leading-none mb-4 group-hover:opacity-70 transition-opacity duration-500">
                When it matters most,<br />
                <span className="italic text-primary">we remember.</span>
              </h2>
              <span className="font-grotesk text-[10px] uppercase tracking-[0.25em] opacity-40 flex items-center gap-2">
                {endOfLifeOpen ? "Close" : "Learn more"}
                <span className={`inline-block transition-transform duration-500 ${endOfLifeOpen ? "rotate-180" : ""}`}>↓</span>
              </span>
            </button>

            <motion.div
              initial={false}
              animate={{ height: endOfLifeOpen ? "auto" : 0, opacity: endOfLifeOpen ? 1 : 0 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="overflow-hidden"
            >
              <div className="pt-8">
                <p className="font-sans text-xl leading-relaxed opacity-70 max-w-2xl mb-6">
                  Long-term Furbaby members receive our deepest discounts on end-of-life
                  care — palliative consultations, quality-of-life assessments, and
                  final farewell services.
                </p>
                <p className="font-sans text-base leading-relaxed opacity-55 max-w-2xl">
                  A Furbaby member will find the hardest day carries the least financial burden. Because years of trust should count for
                  something in every moment — including the last.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── PAUSE SECTION ─────────────────────────────────────── */}
      <section className="py-16 md:py-32 px-4 md:px-20 bg-muted/30 border-b border-border/30">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            <PawPrint className="w-8 h-8 text-primary mb-8 opacity-60" strokeWidth={1.5} />
            <button
              onClick={() => setPauseOpen(o => !o)}
              className="text-left group w-full"
            >
              <h2 className="font-serif text-5xl md:text-7xl tracking-tighter leading-none mb-4 group-hover:opacity-70 transition-opacity duration-500">
                What if I need<br />
                <span className="italic text-primary">to pause?</span>
              </h2>
              <span className="font-grotesk text-[10px] uppercase tracking-[0.25em] opacity-40 flex items-center gap-2">
                {pauseOpen ? "Close" : "Learn more"}
                <span className={`inline-block transition-transform duration-500 ${pauseOpen ? "rotate-180" : ""}`}>↓</span>
              </span>
            </button>

            <motion.div
              initial={false}
              animate={{ height: pauseOpen ? "auto" : 0, opacity: pauseOpen ? 1 : 0 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="overflow-hidden"
            >
              <div className="pt-8">
                <p className="font-sans text-xl leading-relaxed opacity-70 max-w-2xl mb-6">
                  Life happens. If you need to pause your membership, your loyalty level is yours to keep — permanently.
                </p>
                <p className="font-sans text-base leading-relaxed opacity-55 max-w-2xl">
                  Whatever loyalty tier you've reached at the time of pausing, that rate is locked to your account for life — whether you return in six months or six years. Your years of commitment don't disappear. They wait.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── MULTI-PET CALCULATOR ──────────────────────────────── */}
      <section className="py-20 md:py-40 px-4 md:px-20 bg-background">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="mb-12 md:mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-primary mb-4">Multi-Pet Household</p>
            <h2 className="font-serif text-6xl md:text-8xl tracking-tighter leading-none">
              One family,<br /><span className="italic text-primary">one commitment.</span>
            </h2>
            <p className="font-sans text-xl opacity-60 mt-6 max-w-xl leading-relaxed">
              Your second furchild joins at <strong>30% off</strong>. Your third at <strong>40% off</strong>. Your fourth at <strong>50% off</strong>.
              Five or more? <strong>60% off</strong> — because a family this devoted deserves a home price.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            {/* Pet selector */}
            <motion.div
              className="p-6 md:p-10 rounded-[2rem] md:rounded-[3rem] bg-muted/20 border border-border/30"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            >
              {/* Number of pets */}
              <div className="mb-10">
                <p className="font-grotesk text-[9px] uppercase tracking-widest opacity-40 mb-4">Number of furchildren</p>
                <div className="flex items-center gap-3">
                  {[1, 2, 3, 4].map(n => (
                    <button
                      key={n}
                      onClick={() => setNumPets(n)}
                      className={`w-14 h-14 rounded-2xl font-serif text-2xl transition-all duration-400 ${numPets === n ? "bg-primary text-foreground" : "bg-muted/40 hover:bg-muted"}`}
                    >
                      {n}
                    </button>
                  ))}
                  <button
                    onClick={() => setNumPets(5)}
                    className={`px-4 h-14 rounded-2xl font-grotesk text-sm tracking-widest transition-all duration-400 ${numPets === 5 ? "bg-primary text-foreground" : "bg-muted/40 hover:bg-muted"}`}
                  >
                    5+
                  </button>
                </div>
              </div>

              {/* Plan selector per pet — or pack selector for 5+ */}
              {numPets === 5 ? (
                <div>
                  <p className="font-grotesk text-[9px] uppercase tracking-widest opacity-40 mb-4">Household plan level</p>
                  <div className="flex gap-3">
                    {["Steadfast", "Devoted", "Besotted"].map(plan => (
                      <button
                        key={plan}
                        onClick={() => setPackPlan(plan)}
                        className={`flex-1 py-2 px-3 rounded-xl font-grotesk text-[10px] uppercase tracking-widest transition-all duration-300 ${packPlan === plan ? "bg-primary text-foreground" : "bg-muted/40 hover:bg-muted opacity-60"}`}
                      >
                        {plan}
                      </button>
                    ))}
                  </div>
                  <p className="font-sans text-xs opacity-40 mt-4 leading-relaxed">Fixed annual rate covers all pets in one household. No per-head counting.</p>
                </div>
              ) : (
                <div className="flex flex-col gap-5">
                  {Array.from({ length: numPets }).map((_, idx) => (
                    <div key={idx}>
                      <p className="font-grotesk text-[9px] uppercase tracking-widest opacity-40 mb-2">
                        Furchild {idx + 1} {idx > 0 && <span className="text-primary">(−{petDiscount(idx)}%)</span>}
                      </p>
                      <div className="flex gap-3">
                        {["Steadfast", "Devoted", "Besotted"].map(plan => (
                          <button
                            key={plan}
                            onClick={() => setPetPlans(prev => { const n = [...prev]; n[idx] = plan; return n; })}
                            className={`flex-1 py-2 px-3 rounded-xl font-grotesk text-[10px] uppercase tracking-widest transition-all duration-300 ${petPlans[idx] === plan ? "bg-primary text-foreground" : "bg-muted/40 hover:bg-muted opacity-60"}`}
                          >
                            {plan}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Cost breakdown */}
            <motion.div
              className="flex flex-col justify-between"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            >
              <div>
                <p className="font-grotesk text-[9px] uppercase tracking-widest opacity-40 mb-5">Annual total</p>

                {/* Per-pet rows — or pack summary */}
                <div className="flex flex-col gap-4 mb-8">
                  {isPack ? (
                    <div className="flex items-baseline justify-between border-b border-border/20 pb-3">
                      <div>
                        <span className="font-serif text-xl">5+ Household Pack</span>
                        <span className="font-grotesk text-xs opacity-40 ml-2">{packPlan} · all pets</span>
                      </div>
                      <div className="text-right">
                        <span className="font-grotesk text-xs line-through opacity-30 mr-2">S${petFull.toLocaleString()}</span>
                        <span className="font-serif text-2xl text-primary">S${petTotal.toLocaleString()}</span>
                      </div>
                    </div>
                  ) : (
                    Array.from({ length: numPets }).map((_, idx) => {
                      const plan = petPlans[idx] || "Steadfast";
                      const full = PLAN_PRICES[plan];
                      const disc = petDiscount(idx);
                      const price = full * (1 - disc / 100);
                      return (
                        <div key={idx} className="flex items-baseline justify-between border-b border-border/20 pb-3">
                          <div>
                            <span className="font-serif text-xl">Furchild {idx + 1}</span>
                            <span className="font-grotesk text-xs opacity-40 ml-2">{plan}</span>
                          </div>
                          <div className="text-right">
                            {disc > 0 && (
                              <span className="font-grotesk text-xs line-through opacity-30 mr-2">S${full.toLocaleString()}</span>
                            )}
                            <span className="font-serif text-2xl text-primary">S${Math.round(price).toLocaleString()}</span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                <div className="flex items-baseline justify-between mb-2">
                  <span className="font-grotesk text-sm uppercase tracking-widest opacity-50">{isPack ? "Fixed pack rate / year" : "Total / year"}</span>
                  <span className="font-serif text-5xl text-primary">S${Math.round(petTotal).toLocaleString()}</span>
                </div>
                {petSaved > 0 && (
                  <p className="font-grotesk text-sm text-right text-primary opacity-70">
                    You save S${Math.round(petSaved).toLocaleString()} vs. individual pricing
                  </p>
                )}
              </div>

              <motion.div className="mt-16">
                <Link
                  href="/book"
                  className="group inline-flex items-center gap-4 font-grotesk text-sm uppercase tracking-widest hover:opacity-60 transition-opacity duration-500 border-b border-foreground pb-2"
                >
                  Start your journey with us
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform duration-500" />
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── ACCIDENT & ILLNESS COVERAGE ───────────────────────── */}
      <section className="py-20 md:py-32 px-4 md:px-20 bg-foreground text-background">
        <div className="max-w-6xl mx-auto">

          <motion.div
            className="mb-10 md:mb-14"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-primary mb-6">Accident &amp; Illness Cover</p>
            <h2 className="font-serif text-5xl md:text-7xl tracking-tighter leading-none mb-6">
              When life happens,<br />
              <span className="italic text-primary">you're already covered.</span>
            </h2>
            <p className="font-sans text-lg opacity-55 max-w-2xl leading-relaxed mb-6">
              Every Furbaby membership includes in-hospital accident and illness treatment —
              applied directly to your bill at checkout. No forms to file, no waiting for
              reimbursement, no excess to top up.
            </p>
            <button
              onClick={() => setAiCoverOpen(o => !o)}
              className="text-left group"
            >
              <span className="font-grotesk text-[10px] uppercase tracking-[0.25em] opacity-40 flex items-center gap-2">
                {aiCoverOpen ? "Close" : "See coverage by tier"}
                <span className={`inline-block transition-transform duration-500 ${aiCoverOpen ? "rotate-180" : ""}`}>↓</span>
              </span>
            </button>
          </motion.div>

          <motion.div
            initial={false}
            animate={{ height: aiCoverOpen ? "auto" : 0, opacity: aiCoverOpen ? 1 : 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
          <div className="pt-8">
          {/* Coverage tier cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12 md:mb-16">
            {[
              {
                name: "Steadfast", whimsy: "The Steadfast",
                cap: "S$2,000", capSub: "per year",
                equiv: "Surgery, hospitalisation, emergency treatment — covered.",
                featured: false,
              },
              {
                name: "Devoted", whimsy: "The Devoted",
                cap: "S$5,000", capSub: "per year",
                equiv: "Most major procedures covered in full, applied at checkout.",
                featured: true,
              },
              {
                name: "Besotted", whimsy: "The Besotted",
                cap: "S$10,000", capSub: "per year",
                equiv: "Comprehensive cover. Applied directly at checkout.",
                featured: false,
              },
            ].map((tier, i) => (
              <motion.div
                key={tier.name}
                className={`p-6 md:p-8 rounded-3xl border flex flex-col gap-3 ${tier.featured ? "border-primary bg-primary/10" : "border-background/15 bg-background/5"}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] as const }}
              >
                <div>
                  <p className="font-grotesk text-[9px] uppercase tracking-widest opacity-35 mb-0.5">{tier.name}</p>
                  <p className="font-serif text-base opacity-60 italic">{tier.whimsy}</p>
                </div>
                <div>
                  <p className="font-grotesk text-[9px] uppercase tracking-widest opacity-40 mb-1">A&amp;I cover</p>
                  <p className={`font-serif text-4xl md:text-5xl tracking-tighter leading-none ${tier.featured ? "text-primary" : ""}`}>
                    {tier.cap}
                  </p>
                  <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-30 mt-1">{tier.capSub}</p>
                </div>
                <p className="font-sans text-sm opacity-45 leading-relaxed mt-auto pt-2 border-t border-background/10">
                  {tier.equiv}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Furbaby vs. Insurance comparison */}
          <motion.div
            className="border-t border-background/15 pt-12 md:pt-14"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] opacity-35 mb-10">Why not insurance?</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-20">

              <div>
                <p className="font-serif text-xl mb-6 text-primary">Furbaby Membership</p>
                {[
                  "Cover applied at checkout — no claims, no forms",
                  "No excess or deductible to pay before treatment begins",
                  "No breed, age, or pre-existing condition exclusions after enrolment",
                  "Membership fee never rises as your pet ages",
                  "Conditions that develop after enrolment are covered for life",
                  "Loyalty discount grows annually — so senior care costs less, not more",
                ].map((point, i) => (
                  <motion.div
                    key={i}
                    className="flex items-start gap-3 mb-4"
                    initial={{ opacity: 0, x: -12 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: i * 0.07, ease: [0.16, 1, 0.3, 1] as const }}
                  >
                    <span className="text-primary shrink-0 mt-0.5 font-grotesk text-xs">✓</span>
                    <p className="font-sans text-sm opacity-75 leading-snug">{point}</p>
                  </motion.div>
                ))}
              </div>

              <div>
                <p className="font-serif text-xl mb-6 opacity-40">Traditional Pet Insurance</p>
                {[
                  "Pay upfront, claim later — reimbursement can take weeks",
                  "Excess per claim: typically S$100–S$300 before coverage activates",
                  "Pre-existing conditions and many breeds routinely excluded",
                  "Premiums rise steeply with age — often doubling by Year 8",
                  "Ongoing conditions reclassified as 'pre-existing' on renewal",
                  "Coverage narrowed or cancelled as your pet reaches senior years",
                ].map((point, i) => (
                  <motion.div
                    key={i}
                    className="flex items-start gap-3 mb-4"
                    initial={{ opacity: 0, x: 12 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: i * 0.07, ease: [0.16, 1, 0.3, 1] as const }}
                  >
                    <span className="opacity-25 shrink-0 mt-0.5 font-grotesk text-xs">×</span>
                    <p className="font-sans text-sm opacity-30 leading-snug">{point}</p>
                  </motion.div>
                ))}
              </div>

            </div>
          </motion.div>
          </div>
          </motion.div>

        </div>
      </section>

      {/* ── PLAN QUICK REFERENCE ──────────────────────────────── */}
      <section className="py-20 md:py-40 px-4 md:px-20 bg-muted/20 border-t border-border/30">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            className="font-serif text-6xl md:text-8xl tracking-tighter leading-none mb-12 md:mb-20"
            initial={{ y: "110%" }}
            whileInView={{ y: "0%" }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          >
            Choose your<br /><span className="italic text-primary">chapter.</span>
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {PLANS.map((plan, i) => (
              <motion.div
                key={plan.name}
                className="group relative flex flex-col p-6 md:p-10 rounded-[2rem] md:rounded-[3rem] border border-border/40 bg-background hover:border-primary/30 overflow-hidden transition-all duration-700"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.9, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className="absolute inset-0 rounded-[3rem] opacity-0 group-hover:opacity-100 transition-opacity duration-700 bg-gradient-to-br from-primary/16 via-accent/10 to-secondary/16 pointer-events-none" />

                <span className={`relative z-10 self-start mb-4 font-grotesk text-[9px] uppercase tracking-widest px-3 py-1.5 rounded-full ${plan.tag ? "bg-primary/15 text-primary" : "invisible"}`}>
                  {plan.tag ?? "placeholder"}
                </span>

                <p className="relative z-10 font-grotesk text-[10px] uppercase tracking-widest text-primary opacity-60 mb-1">{plan.name}</p>
                <h3 className="relative z-10 font-serif text-4xl mb-1 group-hover:italic transition-all duration-500 group-hover:text-primary">{plan.whimsy}</h3>
                <p className="relative z-10 font-sans text-xs opacity-45 mb-6">{plan.tagline}</p>

                <div className="relative z-10 mb-2">
                  <div className="flex items-baseline gap-2">
                    <span className="font-serif text-4xl text-primary">{plan.monthly}</span>
                    <span className="font-grotesk text-sm font-medium opacity-75 uppercase tracking-widest">/ month</span>
                  </div>
                  <p className="relative z-10 font-grotesk text-[10px] opacity-55 uppercase tracking-widest mt-2 mb-8">{plan.price} · billed as one annual payment</p>
                </div>

                <ul className="relative z-10 flex flex-col gap-3 mb-10 flex-grow">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-start gap-3 font-sans text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary/60 mt-2 flex-shrink-0" />
                      <span className="opacity-65">{f}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  href={`/join/${plan.whimsy.replace("The ", "").toLowerCase()}`}
                  className="relative z-10 group/btn inline-flex items-center gap-3 font-grotesk text-sm uppercase tracking-widest hover:opacity-60 transition-opacity duration-500 border-b border-foreground/20 pb-2 w-fit mt-auto"
                >
                  Be {plan.whimsy.replace("The ", "")}
                  <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-2 transition-transform duration-500" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOOTER ───────────────────────────────────────────── */}
      <footer className="bg-primary py-20 px-4 md:px-20 flex flex-col md:flex-row justify-between items-center gap-8">
        <div>
          <span className="font-serif text-3xl font-bold italic tracking-tighter">Furbaby</span>
          <p className="font-grotesk text-xs uppercase tracking-widest opacity-50 mt-2">© 2024 Furbaby Vet Care · Singapore</p>
        </div>
        <Link
          href="/book"
          className="group inline-flex items-center gap-4 font-grotesk text-sm uppercase tracking-widest hover:opacity-60 transition-opacity duration-500 border-b border-foreground pb-2"
        >
          Start your journey with us
          <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform duration-500" />
        </Link>
      </footer>
    </div>
  );
}
